/**
 * Mandatory compliance copy. Not marketing — these strings are required.
 *
 * Sources, all binding:
 *   KEV_Design_System §06        — the required footer line, verbatim
 *   kev-language-and-guardrails  — §3.1 distress routing, §3.3 18+, §3.4 persona-agnostic
 *   sports-ai-platform-plan §8C  — persistent 18+ / gamble-responsibly footer
 *
 * Do not reword these to fit a layout. Change the layout.
 */

/** §06: required footer line, exact wording. */
export const REQUIRED_FOOTER_LINE = "Always bet responsibly.";

export const AGE_GATE = "18+";

/** §3.1: surfaced alongside Lifeline for distress language, and standing in the footer. */
export const GAMBLING_HELP = {
  name: "Gambling Help Online",
  phone: "1800 858 858",
  href: "https://www.gamblinghelponline.org.au",
  note: "Free, confidential, 24/7.",
} as const;

/** §3.1: self-harm / crisis language routes here, alongside Gambling Help Online. */
export const LIFELINE = {
  name: "Lifeline",
  phone: "13 11 14",
  href: "https://www.lifeline.org.au",
} as const;

/**
 * The product's standing posture, stated plainly.
 * §2: descriptive analytics, not advice to act. §2.3: no personalised stake sizing.
 */
export const POSTURE = "KEV is analytics, not advice.";

/**
 * The fair price, defined once for every surface that shows one.
 *
 * WHY THIS IS A CONSTANT AND NOT PAGE COPY. The metric is 1 ÷ p, in dollars, and
 * it is the subject of an open engagement — `kev-docs/kev-legal-brief-fair-price.md`
 * asks a solicitor to rule on it "wherever it appears". A ruling that changes the
 * wording has to change it everywhere at once, and it cannot do that if each page
 * has written its own paraphrase.
 *
 * The wording follows the shipped `fairPriceMeans` string in
 * `nba/lambda_nba_chat.py`, which the /team paragraphs already track. All three
 * constraints that brief calls load-bearing are here: it is OUR arithmetic, it is
 * NOT a market price or an odds quote, and the comparison is handed back to the
 * reader rather than made for them.
 *
 * The banned move, stated so the next author does not reinvent it: never say a
 * price "is value" or "is an edge", however reasonably phrased. Report the
 * conversion and stop.
 */
export const FAIR_PRICE = {
  /** One line, for beside a toggle or a column heading. */
  short:
    "Break-even decimal odds implied by Kev’s own probability — not a market price.",
  /** The full caveat, for any page that shows one. */
  body:
    "A fair price is Kev’s probability turned into what a bet would have to pay to break even if that probability is right: a 40% chance is about $2.50. It is arithmetic on Kev’s own number and nothing else. No odds feed is connected, so Kev cannot see what anyone is offering and cannot tell you whether the price in front of you beats this one — holding the two up against each other is your step.",
} as const;

/**
 * Accuracy and liability, carried on every page beside the betting and wellbeing
 * warnings. It belongs to that family: what this product can and can't do for you.
 *
 * Sport data arrives late, gets corrected, and sometimes disagrees with itself,
 * so a flat accuracy promise is one we couldn't keep.
 *
 * Deliberately formal (Ned, 2026-07-31). This is the paragraph that has to hold
 * up if a decision made on a KEV number goes badly, so it borrows the exact
 * terms the contract uses — "as is", "reasonable care", "no warranty",
 * "reliance" — and mirrors Terms §3 and §11. Every one of those phrases is
 * load-bearing; an absolute accuracy claim here would cut against both.
 */
export const ACCURACY =
  "Information in the Service is provided on an “as is” basis. We take reasonable care to keep it accurate, complete and current, but give no warranty to that effect and accept no liability for any decision made in reliance on it.";

/*
 * NO_MARKET_DATA ("No odds feed is connected yet…") left the site chrome on
 * 2026-07-31, at Ned's direction, so the shopfront reads positively.
 *
 * Three things that decision did NOT touch:
 *   - kev-language-and-guardrails §2 (build-state note) and §7 still bind Kev in
 *     conversation. Kev may never invent, estimate or infer a market price, line
 *     or implied probability. That rule lives in the model prompt, so deleting a
 *     marketing string can't weaken it.
 *   - Terms §2 still says plainly that KEV isn't a bookmaker and reports its own
 *     numbers.
 *   - ticker.ts still bans prices, lines and probabilities from the banner.
 *
 * Dropping the negation from the shopfront was a tone call. It isn't licence to
 * imply anywhere that a market feed exists.
 */
